/**
 * ORDEM ALFABÉTICA PADRÃO DO SISTEMA — a "configuração" única de ordenação.
 *
 * Toda lista de PESSOAS ou de CADASTROS identificados por nome (colaboradores,
 * gestores, participantes, candidatos, cargos, setores...) sai em ordem
 * crescente A→Z, igual à agenda do Planejamento:
 *
 *   a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
 *
 * - "pt-BR" + sensitivity "base": acento e maiúscula não empurram o nome para
 *   o fim da lista ("Álvaro" fica perto de "Adolfo", "ana" junto de "Ana").
 * - numeric: "Turma 2" vem antes de "Turma 10".
 *
 * A ordenação é feita NA TELA, no ponto de exibição: é a garantia. Não depende
 * de a API ter vindo ordenada (o ORDER BY do banco usa a collation do
 * servidor, que nem sempre trata acento como o português espera).
 *
 * Listas cuja ordem tem SIGNIFICADO (datas, históricos, rankings da IA,
 * perguntas de formulário, níveis de cargo, escalas) NÃO passam por aqui.
 */

const COLLATOR = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

/** Compara dois nomes em ordem alfabética brasileira (A→Z). Vazio vai para o fim. */
export const compararNomes = (a: unknown, b: unknown): number => {
  const x = String(a ?? "").trim();
  const y = String(b ?? "").trim();
  // Registro sem nome não pode abrir a lista (o "" viria antes do "A").
  if (!x && y) return 1;
  if (x && !y) return -1;
  return COLLATOR.compare(x, y);
};

/**
 * Devolve uma CÓPIA da lista em ordem alfabética pelo nome escolhido.
 * Nunca muta o array original (outro código pode usá-lo por índice).
 */
export function ordenarPorNome<T>(lista: readonly T[], nome: (item: T) => unknown): T[] {
  return [...lista].sort((a, b) => compararNomes(nome(a), nome(b)));
}

/** Atalho para lista de textos (setores, departamentos, cargos...). */
export function ordenarTextos<T extends string>(lista: readonly T[]): T[] {
  return [...lista].sort(compararNomes);
}

/**
 * LANÇAMENTOS POR COLABORADOR (benefícios, documentos, ponto, férias, horas
 * extras, plano de ação, demissões…): A→Z pelo NOME da pessoa e, dentro da
 * mesma pessoa, em ordem CRESCENTE de data — o dia 1 antes do dia 2, como se
 * lê uma folha. Empate total cai no id, também crescente.
 *
 * Antes (até 2026-09-16) essas abas seguiam a ordem de registro, do mais novo
 * para o mais antigo: quem procurava um colaborador tinha de caçar as linhas
 * dele espalhadas pela lista inteira.
 */
export function ordenarRegistros<T extends Record<string, unknown>>(
  lista: readonly T[],
  campos: { nome: string; data?: string },
): T[] {
  const dataDe = (r: T) => String((campos.data ? r[campos.data] : "") ?? "").slice(0, 10);
  return [...lista].sort(
    (a, b) =>
      compararNomes(a[campos.nome], b[campos.nome]) ||
      // "AAAA-MM-DD" compara certo como texto; data vazia vai para o fim.
      (dataDe(a) && dataDe(b) ? dataDe(a).localeCompare(dataDe(b)) : dataDe(a) ? -1 : dataDe(b) ? 1 : 0) ||
      (Number(a.id) || 0) - (Number(b.id) || 0),
  );
}
